import { Button, Divider, Form, Modal, Tabs } from "antd";
import { message } from "utils/ciq-message";
import { useCallback, useContext, useEffect, useState } from "react";
import { ProjectContext } from "context/ProjectProvider";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import { gql } from "@apollo/client";
import { MUTATION_CREATE_FEATURE_INSTANCE } from "entity-app/graphQL/ciq-feature-mutations";
import { MaterialExtractSpecType } from "../model";
import MaterialFormBody from "./material-form-body";
import "./create-material-model.scss";
import { ErrorMessages } from "constants/index";
import { validatorForFieldMultiValues } from "utils/utils";

type FormValues = Record<string, any>;

type CreateMaterialsMultiModalProps = {
  open: boolean;
  onCancel: () => void;
  onComplete: () => void;
  selectedRows: MaterialExtractSpecType[];
  materialHeaderMap: any;
};

export default function CreateMaterialsMultiModal({
  open,
  onCancel,
  onComplete,
  selectedRows,
  materialHeaderMap
}: CreateMaterialsMultiModalProps) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [formsData, setFormsData] = useState<Record<number, FormValues>>({});
  const [specIdsMap, setSpecIdsMap] = useState<Record<number, string[]>>({});

  const { gqlClientForProject, tokenRetrievalState } =
    useContext(ProjectContext);

  const [createEntityMutation] = useCIQMutation(
    gql(MUTATION_CREATE_FEATURE_INSTANCE),
    { client: gqlClientForProject }
  );

  // Initialize form data from selected rows
  useEffect(() => {
    if (open && selectedRows?.length > 0) {
      const initialFormsData: Record<number, FormValues> = {};
      const initialSpecIds: Record<number, string[]> = {};
      selectedRows.forEach((row, i) => {
        initialFormsData[i] = {
          title: row.title || "",
          tag_name: row.tag || "",
          manufacturer: row.manufacturer || "",
          description: ""
        };
        initialSpecIds[i] = [];
      });
      setFormsData(initialFormsData);
      setSpecIdsMap(initialSpecIds);
      setActiveIndex(0);
      form.setFieldsValue(initialFormsData[0]);
    }
  }, [open, selectedRows, form]);

  // Save current form state before switching
  const saveCurrentFormState = useCallback(() => {
    const currentValues = form.getFieldsValue();
    setFormsData((prev) => ({
      ...prev,
      [activeIndex]: currentValues
    }));
  }, [form, activeIndex]);

  const switchToItem = useCallback(
    (newIndex: number) => {
      if (newIndex === activeIndex) return;
      saveCurrentFormState();
      setActiveIndex(newIndex);
      const savedData = formsData[newIndex] || {
        title: selectedRows[newIndex]?.title || "",
        tag_name: selectedRows[newIndex]?.tag || "",
        manufacturer: selectedRows[newIndex]?.manufacturer || "",
        description: ""
      };
      form.setFieldsValue(savedData);
    },
    [activeIndex, form, formsData, saveCurrentFormState, selectedRows]
  );

  const selectedSpecIds = specIdsMap[activeIndex] || [];
  const setSelectedSpecIds = useCallback(
    (idsOrUpdater: React.SetStateAction<string[]>) => {
      setSpecIdsMap((prev) => {
        const current = prev[activeIndex] || [];
        const next =
          typeof idsOrUpdater === "function"
            ? idsOrUpdater(current)
            : idsOrUpdater;
        return { ...prev, [activeIndex]: next };
      });
    },
    [activeIndex]
  );

  const isDuplicateTitleInFormData = async (allFormsData: any) => {
    // Validate duplicate titles in formsData
    const titles: string[] = Object.values(allFormsData).map((data: any) =>
      (data?.title || "").trim()
    );

    const uniqueTitles = [...new Set(titles)];
    if (uniqueTitles.length !== titles.length) return true;

    try {
      await validatorForFieldMultiValues(
        "title",
        uniqueTitles,
        "MATERIALS",
        tokenRetrievalState.token,
        ErrorMessages.MaterialTitleMsg
      );

      return false;
      // eslint-disable-next-line no-empty
    } catch {}
    return true;
  };

  const handleSubmit = async () => {
    try {
      // Save current form state first
      saveCurrentFormState();
      const currentValues = form.getFieldsValue();
      const allFormsData = {
        ...formsData,
        [activeIndex]: currentValues
      };

      if (await isDuplicateTitleInFormData(allFormsData)) {
        message.error(ErrorMessages.MaterialTitleMsg);
        return;
      }

      setLoading(true);

      const featureInstances = selectedRows.map((row, i) => {
        const data = allFormsData[i] || {};
        const instance: any = {
          extraction_data_id: row.id,
          title: data.title || row.title,
          tag_name: data.tag_name || row.tag,
          manufacturer: data.manufacturer || row.manufacturer,
          description: data.description,
          spec_sections: specIdsMap[i] || []
        };

        if (data.assignee) {
          instance.assignee = data.assignee;
        }
        if (data.gc_representative) {
          instance.gc_representative = data.gc_representative;
        }

        return instance;
      });

      const variables = {
        feature_type: "MATERIALS",
        feature_instances: featureInstances
      };

      const response: any = await createEntityMutation({ variables });

      if (response?.data?.insert_feature_instances_multi?.ids?.length) {
        const count = response.data.insert_feature_instances_multi.ids.length;
        message.success(`${count} materials were added to the log.`);
        onComplete();
      } else {
        message.error("Unable to create Materials.");
      }
    } catch (error) {
      console.error("Error creating materials:", error);
    }
    setLoading(false);
  };

  return (
    <Modal
      className="create-material-modal"
      title={
        <div className="flex items-baseline gap-2">
          <span className="text-base font-bold text-ciq-gray-950-a90">
            Create Materials
          </span>
          <span className="text-xs font-normal ml-2 mr-2">|</span>
          <span className="text-xs font-normal text-black text-opacity-60">
            {selectedRows.length} Schedules Selected
          </span>
        </div>
      }
      open={open}
      width={800}
      centered
      destroyOnHidden
      onCancel={onCancel}
      styles={{ body: { padding: 0 } }}
      footer={[
        <section className="flex justify-end gap-2" key="footer">
          <Button onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="primary"
            loading={loading}
            disabled={loading}
            onClick={handleSubmit}
          >
            Confirm &amp; Create
          </Button>
        </section>
      ]}
    >
      <Divider className="!m-0" />
      <Tabs
        tabPlacement="start"
        activeKey={String(activeIndex)}
        onChange={(key) => switchToItem(Number(key))}
        className="create-material-tabs h-[480px]"
        items={selectedRows.map((row, i) => ({
          key: String(i),
          label: (
            <div className="truncate max-w-[290px]">
              {row.table_title || row.title || `Material ${i + 1}`}
            </div>
          ),
          children: (
            <div className="overflow-y-auto h-full p-5">
              <Form
                form={form}
                layout="vertical"
                preserve={false}
                validateTrigger="onSubmit"
              >
                <MaterialFormBody
                  form={form}
                  materialHeaderMap={materialHeaderMap}
                  selectedSpecIds={selectedSpecIds}
                  setSelectedSpecIds={setSelectedSpecIds}
                  token={tokenRetrievalState?.token}
                />
              </Form>
            </div>
          )
        }))}
      />
      <Divider className="!m-0" />
    </Modal>
  );
}
